import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';

const beforeAfterData = [
  {
    id: 1,
    title: "House Wash - Cedar Restoration",
    description: "Complete cedar shake restoration bringing back the natural beauty",
    image: "https://customer-assets.emergentagent.com/job_63cc0cbd-291e-4bc6-b7a0-dfd1144cdf11/artifacts/g4cshplx_House%20Wash%204.jpg",
    service: "House Washing"
  },
  {
    id: 2,
    title: "Cedar Siding Transformation",
    description: "Dark, weathered siding restored to its original warm tones",
    image: "https://customer-assets.emergentagent.com/job_63cc0cbd-291e-4bc6-b7a0-dfd1144cdf11/artifacts/7ocpcsnh_Cedar%20Wash%205.jpg",
    service: "House Washing"
  },
  {
    id: 3,
    title: "Deck Restoration",
    description: "Gray, weathered deck brought back to rich, beautiful wood",
    image: "https://customer-assets.emergentagent.com/job_63cc0cbd-291e-4bc6-b7a0-dfd1144cdf11/artifacts/dvjt0ytj_deck%20wash%20new%202jpg.jpg",
    service: "Deck Cleaning"
  },
  {
    id: 4,
    title: "Window Cleaning",
    description: "Crystal clear windows and spotless sills for a perfect view",
    image: "/images/before-after/window-cleaning.jpg",
    service: "Window Cleaning"
  },
  {
    id: 5,
    title: "Gutter Cleaning",
    description: "Clogged gutters cleared of debris for proper drainage",
    image: "/images/before-after/gutter-cleaning.jpg",
    service: "Gutter Cleaning"
  },
  {
    id: 6,
    title: "Solar Panel Cleaning",
    description: "Dirty solar panels restored to maximize energy efficiency",
    image: "/images/before-after/solar-panels.jpg",
    service: "Solar Panel Cleaning"
  }
];

const BeforeAfter = () => {
  const [selectedImage, setSelectedImage] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);

  const openLightbox = (index) => {
    setCurrentIndex(index);
    setSelectedImage(beforeAfterData[index]);
  };

  const closeLightbox = () => {
    setSelectedImage(null);
  };

  const nextImage = () => {
    const newIndex = (currentIndex + 1) % beforeAfterData.length;
    setCurrentIndex(newIndex);
    setSelectedImage(beforeAfterData[newIndex]);
  };

  const prevImage = () => {
    const newIndex = (currentIndex - 1 + beforeAfterData.length) % beforeAfterData.length;
    setCurrentIndex(newIndex);
    setSelectedImage(beforeAfterData[newIndex]);
  };

  return (
    <section id="before-after" className="py-20 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="text-center mb-16">
          <span className="inline-block px-4 py-1 bg-blue-100 text-blue-600 rounded-full text-sm font-semibold mb-4">
            Our Results
          </span>
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Before & After Transformations
          </h2>
          <p className="text-lg text-gray-600 max-w-2xl mx-auto">
            See the remarkable difference our professional cleaning services make. 
            Real results from real projects in the Hamptons.
          </p>
        </div>

        {/* Gallery Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {beforeAfterData.map((item, index) => (
            <div
              key={item.id}
              data-testid={`before-after-card-${item.id}`}
              className="group relative bg-white rounded-2xl overflow-hidden shadow-lg hover:shadow-2xl transition-all duration-300 cursor-pointer transform hover:-translate-y-1"
              onClick={() => openLightbox(index)}
            >
              {/* Image Container */}
              <div className="relative aspect-square overflow-hidden">
                <img
                  src={item.image}
                  alt={item.title}
                  className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                />
                {/* Overlay on Hover */}
                <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />

                {/* View Button */}
                <div className="absolute inset-0 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                  <span className="px-6 py-3 bg-white text-gray-900 font-semibold rounded-full shadow-lg transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                    View Full Image
                  </span>
                </div>
              </div>

              {/* Card Content */}
              <div className="p-5">
                <h3 className="text-lg font-bold text-gray-900 mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600">{item.description}</p>
              </div>
            </div>
          ))}
        </div>

        {/* CTA */}
        <div className="text-center mt-12">
          <p className="text-gray-600 mb-4">Ready to transform your property?</p>
          <a
            href="#quote"
            data-testid="before-after-cta"
            className="inline-flex items-center px-8 py-4 bg-blue-600 text-white font-semibold rounded-full hover:bg-blue-700 transition-colors shadow-lg hover:shadow-xl"
          >
            Get Your Free Quote
          </a>
        </div>
      </div>

      {/* Lightbox Modal */}
      {selectedImage && (
        <div 
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4"
          onClick={closeLightbox}
          data-testid="lightbox-modal"
        >
          {/* Close Button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 p-2 text-white hover:text-gray-300 transition-colors z-10"
            data-testid="lightbox-close"
          >
            <X size={32} />
          </button>

          {/* Navigation Arrows */}
          <button
            onClick={(e) => { e.stopPropagation(); prevImage(); }}
            className="absolute left-4 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
            data-testid="lightbox-prev"
          >
            <ChevronLeft size={32} />
          </button>
          <button
            onClick={(e) => { e.stopPropagation(); nextImage(); }}
            className="absolute right-4 p-3 bg-white/10 hover:bg-white/20 text-white rounded-full transition-colors"
            data-testid="lightbox-next"
          >
            <ChevronRight size={32} />
          </button>

          {/* Image Container */}
          <div 
            className="max-w-5xl max-h-[85vh] relative"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={selectedImage.image}
              alt={selectedImage.title}
              className="max-w-full max-h-[85vh] object-contain rounded-lg shadow-2xl"
            />
            
            {/* Caption */}
            <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-6 rounded-b-lg">
              <h3 className="text-xl font-bold text-white mb-1">{selectedImage.title}</h3>
              <p className="text-gray-300">{selectedImage.description}</p>
            </div>
          </div>

          {/* Image Counter */}
          <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-white text-sm">
            {currentIndex + 1} / {beforeAfterData.length}
          </div>
        </div>
      )}
    </section>
  );
};

export default BeforeAfter;
